import { useState, useMemo, lazy, Suspense } from 'react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Search, Loader2 } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';

// Hebrew keywords mapping for common icons
const HEBREW_KEYWORDS: Record<string, string[]> = {
  'cat': ['חתול'],
  'dog': ['כלב'],
  'bird': ['ציפור'],
  'fish': ['דג'],
  'rabbit': ['ארנב'],
  'star': ['כוכב'],
  'heart': ['לב', 'אהבה'],
  'sparkles': ['נצנוצים', 'קסם'],
  'crown': ['כתר', 'מלך', 'מלכה'],
  'gem': ['יהלום', 'אבן'],
  'gamepad-2': ['משחק', 'גיימינג'],
  'music': ['מוזיקה', 'שיר'],
  'palette': ['צבעים', 'ציור', 'אמנות'],
  'camera': ['מצלמה', 'צילום'],
  'book': ['ספר', 'קריאה'],
  'rocket': ['רקטה', 'חלל'],
  'plane': ['מטוס', 'טיסה'],
  'car': ['מכונית', 'רכב'],
  'bike': ['אופניים'],
  'ship': ['ספינה', 'סירה'],
  'sun': ['שמש'],
  'moon': ['ירח', 'לילה'],
  'cloud': ['ענן'],
  'flower-2': ['פרח'],
  'tree-deciduous': ['עץ'],
  'smile': ['חיוך', 'שמח'],
  'party-popper': ['מסיבה', 'חגיגה'],
  'gift': ['מתנה'],
  'trophy': ['גביע', 'ניצחון'],
  'medal': ['מדליה', 'פרס'],
  'baby': ['תינוק', 'ילד'],
  'user': ['משתמש', 'אדם'],
  'users': ['משתמשים', 'אנשים', 'משפחה'],
  'home': ['בית'],
  'school': ['בית ספר', 'לימודים'],
  'pencil': ['עיפרון', 'כתיבה'],
  'scissors': ['מספריים'],
  'soccer': ['כדורגל'],
  'basketball': ['כדורסל'],
  'tennis': ['טניס'],
  'football': ['פוטבול'],
  'swim': ['שחייה'],
  'dumbbell': ['משקולת', 'ספורט'],
  'pizza': ['פיצה', 'אוכל'],
  'ice-cream-cone': ['גלידה'],
  'cake': ['עוגה', 'יום הולדת'],
  'cookie': ['עוגייה'],
  'apple': ['תפוח'],
  'banana': ['בננה'],
  'carrot': ['גזר'],
  'egg': ['ביצה'],
  'coffee': ['קפה'],
  'cup-soda': ['שתייה', 'מיץ'],
  'utensils': ['סכום', 'אוכל'],
  'tv': ['טלוויזיה'],
  'monitor': ['מסך', 'מחשב'],
  'smartphone': ['טלפון', 'פלאפון'],
  'headphones': ['אוזניות'],
  'guitar': ['גיטרה'],
  'piano': ['פסנתר'],
  'drum': ['תוף'],
  'mic': ['מיקרופון', 'שירה'],
  'butterfly': ['פרפר'],
  'bug': ['חרק'],
  'snail': ['חילזון'],
  'turtle': ['צב'],
  'squirrel': ['סנאי'],
  'paw-print': ['כפה', 'חיה'],
  'bone': ['עצם'],
  'feather': ['נוצה'],
  'leaf': ['עלה'],
  'mountain': ['הר'],
  'waves': ['גלים', 'ים'],
  'rainbow': ['קשת'],
  'snowflake': ['פתית שלג', 'שלג'],
  'umbrella': ['מטריה', 'גשם'],
  'wind': ['רוח'],
  'flame': ['אש', 'להבה'],
  'droplet': ['טיפה', 'מים'],
  'lightbulb': ['נורה', 'רעיון'],
  'key': ['מפתח'],
  'lock': ['מנעול'],
  'bell': ['פעמון'],
  'alarm-clock': ['שעון מעורר'],
  'watch': ['שעון יד'],
  'glasses': ['משקפיים'],
  'shirt': ['חולצה'],
  'footprints': ['עקבות', 'רגליים'],
  'hand': ['יד'],
  'thumbs-up': ['לייק', 'אהבתי'],
  'clover': ['תלתן', 'מזל'],
  'wand': ['שרביט', 'קסם'],
  'ghost': ['רוח רפאים'],
  'skull': ['גולגולת'],
  'sword': ['חרב'],
  'shield': ['מגן'],
  'flag': ['דגל'],
  'map': ['מפה'],
  'compass': ['מצפן'],
  'anchor': ['עוגן'],
  'tent': ['אוהל', 'קמפינג'],
  'campfire': ['מדורה'],
  'flashlight': ['פנס'],
  'binoculars': ['משקפת'],
};

// Popular icons to show by default
const POPULAR_ICON_KEYS = [
  'cat', 'dog', 'bird', 'fish', 'rabbit',
  'star', 'heart', 'sparkles', 'crown', 'gem',
  'gamepad-2', 'music', 'palette', 'camera', 'book',
  'rocket', 'plane', 'car', 'bike', 'ship',
  'sun', 'moon', 'cloud', 'flower-2', 'tree-deciduous',
  'smile', 'party-popper', 'gift', 'trophy', 'medal',
  'baby', 'user', 'users', 'home', 'soccer'
];

interface IconPickerProps {
  selectedIcon: string | null;
  onSelectIcon: (iconName: string) => void;
  userColor: string;
}

// Convert kebab-case to PascalCase for component import
const toPascalCase = (str: string) => {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

// Icon button component with lazy loading
function IconButton({ 
  iconKey, 
  isSelected, 
  onSelect, 
  userColor 
}: { 
  iconKey: string; 
  isSelected: boolean; 
  onSelect: () => void; 
  userColor: string;
}) {
  const iconImport = dynamicIconImports[iconKey as keyof typeof dynamicIconImports];
  if (!iconImport) return null;
  
  const LucideIcon = lazy(iconImport);
  
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200",
        "hover:scale-110 hover:shadow-md",
        isSelected
          ? "ring-2 ring-offset-2 shadow-lg"
          : "bg-muted/50 hover:bg-muted"
      )}
      style={{
        backgroundColor: isSelected ? userColor : undefined,
        color: isSelected ? 'white' : undefined,
        '--tw-ring-color': userColor
      } as React.CSSProperties}
      title={toPascalCase(iconKey)}
    >
      <Suspense fallback={<Loader2 className="w-4 h-4 animate-spin" />}>
        <LucideIcon className="w-6 h-6" />
      </Suspense>
    </button>
  );
}

export function IconPicker({ selectedIcon, onSelectIcon, userColor }: IconPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Get all icon keys
  const allIconKeys = useMemo(() => Object.keys(dynamicIconImports), []);

  // Filter icons based on search
  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) {
      return POPULAR_ICON_KEYS;
    }

    const query = searchQuery.toLowerCase().trim();
    
    return allIconKeys.filter(iconKey => {
      // Check icon key (kebab-case)
      if (iconKey.includes(query)) {
        return true;
      }
      
      // Check PascalCase name
      const pascalName = toPascalCase(iconKey).toLowerCase();
      if (pascalName.includes(query)) {
        return true;
      }
      
      // Check Hebrew keywords
      const hebrewKeywords = HEBREW_KEYWORDS[iconKey];
      if (hebrewKeywords && hebrewKeywords.some(kw => kw.includes(query))) {
        return true;
      }
      
      return false;
    }).slice(0, 60); // Limit results for performance
  }, [searchQuery, allIconKeys]);

  const handleSelectIcon = (iconKey: string) => {
    // Store as PascalCase for use with lucide-react direct imports
    onSelectIcon(toPascalCase(iconKey));
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="חפש אייקון... (cat, חתול, star, כדורגל)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          dir="auto"
        />
      </div>
      
      <ScrollArea className="h-[200px] rounded-lg border p-3">
        <div className="grid grid-cols-5 gap-2 p-1">
          {filteredIcons.map((iconKey) => (
            <IconButton
              key={iconKey}
              iconKey={iconKey}
              isSelected={selectedIcon === toPascalCase(iconKey)}
              onSelect={() => handleSelectIcon(iconKey)}
              userColor={userColor}
            />
          ))}
        </div>
        {filteredIcons.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            לא נמצאו אייקונים
          </div>
        )}
      </ScrollArea>
      
      {selectedIcon && (
        <p className="text-xs text-muted-foreground text-center">
          נבחר: {selectedIcon}
        </p>
      )}
    </div>
  );
}
